import axios from 'axios';

import userStore, { USER_TOKEN } from '@/stores/user';
import { message } from 'antd';
import isMobile from 'is-mobile';
import rum, { EReportModule } from '@/plugins/rum';

/**
 * 判断是否需要跳转登录页。
 * @returns {boolean} 当前页面是否应跳转到登录。
 */
const judgeIsJumpLogin = () => {
  const url = window?.location?.pathname || '';

  // 登录页
  if (url.includes('/login')) {
    return false;
  }

  // 免登的对话详情页
  const botDetail = /^\/outer\/bot(?:\/([^\/]+))?\/?$/i;
  if (botDetail.test(url)) {
    return false;
  }

  return true;
};

/**
 * 识别整批导入接口，避免把文件、正文和完整结果写入遥测。
 * @param {string | undefined} url 当前请求地址。
 * @returns {boolean} 是否为新增整批导入接口。
 */
const isArticleImportRequest = (url?: string): boolean => /(?:^|\/)company\/article\/upload\/(?:rules|batches)(?:\/|$)/.test((url || '').split('?')[0]);

/**
 * 为新导入接口提取固定安全字段，不输出请求或完整响应。
 * @param {string | undefined} url 当前接口地址。
 * @param {{log_id?: string, err_no?: number, data?: {errorCode?: string}}} response 业务响应。
 * @returns {Record<string, unknown>} 仅 URL、Trace ID 和安全错误分类。
 */
const importTelemetry = (url: string | undefined, response?: { log_id?: string; err_no?: number; data?: { errorCode?: string } }): Record<string, unknown> => ({
  url: (url || '').split('?')[0],
  traceId: response?.log_id,
  errorCode: /^[A-Z][A-Z0-9_]{0,63}$/.test(response?.data?.errorCode || '') ? response?.data?.errorCode : String(response?.err_no ?? 'QUERY_ERROR'),
});

const service = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'X-client-type': isMobile() ? 'MOBILE' : 'PC',
  }
});

service.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
service.interceptors.response.use(
  /**
   * 解包业务响应，并为文章批量导入错误生成脱敏遥测。
   * @param {import('axios').AxiosResponse} res 原始 HTTP 响应。
   * @returns {Promise<unknown>} 业务 data；失败时拒绝并交给调用方处理。
   */
  async (res) => {
    const { isSilence = false, responseType } = res.config;
    // 直接返回二进制
    if (responseType === 'blob') {
      return res.data;
    }
    if (res.data.err_no === 10005 && window.location.pathname !== '/login') {
      userStore.setUserInfo(null);
      if (judgeIsJumpLogin()) {
        window.location.href = `/login?redirect=${encodeURIComponent(location.href)}`;
      }
      return Promise.reject(res.data?.err_msg || '登录已过期');
    }
    if (res.data.err_no !== 0) {
      // 报错统一提示
      if (!isSilence) {
        message.warning(res.data.err_msg || '服务器开小差了, 请稍后再试!');
      }
      rum.report?.error({
        module: EReportModule.Api,
        keyword: '请求错误',
        extra: isArticleImportRequest(res.config.url) ? importTelemetry(res.config.url, res.data) : { url: res.config.url, request: res.config.data, response: res.data }
      });
      return Promise.reject(res.data);
    }
    return res.data.data;
  },
  /**
   * 处理传输异常及登录过期，导入接口不记录文件或完整结果。
   * @param {import('axios').AxiosError} error HTTP 或网络异常。
   * @returns {Promise<never>} 保留导入保存前拒绝业务码，其他异常交给业务流程判断是否需要核实。
   */
  async (error) => {
    const { isSilence = false } = error.config || {};
    if (isArticleImportRequest(error.config?.url)) {
      rum.report?.error({ module: EReportModule.Api, keyword: '请求错误', extra: importTelemetry(error.config?.url, error.response?.data) });
    }
    // 导入10004仅表示任何保存前拒绝，HTTP状态不改变其含义；通用异常仍保留未知语义。
    if (isArticleImportRequest(error.config?.url) && error.response?.data?.err_no === 10004) {
      if (!isSilence) {
        message.warning(error.response.data.err_msg || '服务器开小差了, 请稍后再试!');
      }
      return Promise.reject(error.response.data);
    }
    if (error?.response?.status === 401) {
      // 已登录过则提示
      if (localStorage.getItem(USER_TOKEN)) {
        message.warning('登录已过期');
        if (judgeIsJumpLogin()) {
          setTimeout(() => {
            window.location.href = `/login?redirect=${encodeURIComponent(location.href)}`;
          }, 300);
        }
      }
      userStore.setUserInfo(null);
      return Promise.reject(error);
    }
    // 报错统一提示
    if (!isSilence) {
      message.warning(error?.response?.data?.err_msg || '服务器开小差了, 请稍后再试!');
    }
    return Promise.reject(error);
  }
);

export default service;
